import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';

import type { CreateAdminUserInput } from '../api';

interface FieldErrors {
  name?: string;
  email?: string;
}

interface DraftState {
  name: string;
  email: string;
}

const EMPTY_DRAFT: DraftState = { name: '', email: '' };

export function CreateUserDialog({
  open,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: CreateAdminUserInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});

  function handleClose(next: boolean): void {
    if (!next) {
      setDraft(EMPTY_DRAFT);
      setErrors({});
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    const name = draft.name.trim();
    const email = draft.email.trim();
    if (name.length === 0) next.name = t('routes.admin.users.create.errors.nameRequired');
    if (email.length === 0) next.email = t('routes.admin.users.create.errors.emailRequired');
    else if (!/.+@.+\..+/.test(email)) next.email = t('routes.admin.users.create.errors.emailInvalid');
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({ name, email });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.users.create.title')}</DialogTitle>
          <DialogDescription>{t('routes.admin.users.create.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="admin-user-name"
            label={t('routes.admin.users.create.fields.name')}
            required
            error={errors.name}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.name}
                onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                autoComplete="off"
              />
            )}
          </FormField>
          <FormField
            id="admin-user-email"
            label={t('routes.admin.users.create.fields.email')}
            required
            error={errors.email}
          >
            {(fp) => (
              <Input
                {...fp}
                type="email"
                value={draft.email}
                onChange={(e) => setDraft((p) => ({ ...p, email: e.target.value }))}
                autoComplete="off"
              />
            )}
          </FormField>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.users.create.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={pending}
            >
              {t('routes.admin.users.create.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {t('routes.admin.users.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
